import React, { useRef, useEffect, useState, useCallback } from 'react'
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  ImperativePanelHandle,
  ImperativePanelGroupHandle,
  getPanelGroupElement,
} from 'react-resizable-panels'
import classnames from 'classnames/bind'
import styles from './styles.scss'
import { Icon, SlottableComponent, AutomationTarget } from '@adc/ui-core'

const cx = classnames.bind(styles)

export interface SidePanelConfig {
  defaultSize?: number
  collapsible?: boolean
  defaultCollapsed?: boolean
}

export interface ResizablePanelGroupProps
  extends AutomationTarget,
    SlottableComponent<'left' | 'main' | 'right'> {
  cls?: Record<string, string>
  leftPanel?: SidePanelConfig
  rightPanel?: SidePanelConfig
}

const COMPACT_PANEL_WIDTH_PX = 300

// DS breakpoints: collapse the right panel below laptop, and additionally
// collapse the left panel below tablet.
const RIGHT_COLLAPSE_BREAKPOINT = 992 // laptop
const LEFT_COLLAPSE_BREAKPOINT = 760 // tablet

interface CollapsibleHandleProps {
  panelRef: React.RefObject<ImperativePanelHandle | null>
  isCollapsed: boolean
  side: 'left' | 'right'
  id: string
  cls?: Record<string, string>
  onDragging?: (isDragging: boolean) => void
  getExpandSize?: () => number | undefined
}

const CollapsibleHandle: React.FC<CollapsibleHandleProps> = ({
  panelRef,
  isCollapsed,
  side,
  id,
  cls = {},
  onDragging,
  getExpandSize,
}) => {
  const toggle = useCallback(() => {
    if (!panelRef.current) return
    if (panelRef.current.isCollapsed()) {
      const size = getExpandSize?.()
      if (size) {
        panelRef.current.resize(size)
      } else {
        panelRef.current.expand()
      }
    } else {
      panelRef.current.collapse()
    }
  }, [panelRef, getExpandSize])

  const handleDoubleClick = useCallback(() => toggle(), [toggle])

  const handleTabClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      toggle()
    },
    [toggle]
  )

  const iconName = side === 'left' ? 'sidebar' : 'sidebar-flip'

  // Tab always sits on the main-content side of the resizer:
  // left panel's handle renders to the right, right panel's handle to the left.
  const tabSide = side === 'left' ? 'right' : 'left'

  return (
    <PanelResizeHandle
      id={id}
      className={cx(
        'resize-handle',
        `resize-handle--${side}`,
        { 'resize-handle--collapsed': isCollapsed },
        cls.resizeHandle
      )}
      onDoubleClick={handleDoubleClick}
      onDragging={onDragging}
    >
      <button
        className={cx('collapsed-tab', `collapsed-tab--${tabSide}`)}
        onClick={handleTabClick}
        aria-label={
          isCollapsed ? `Expand ${side} panel` : `Collapse ${side} panel`
        }
        type='button'
        data-elm-id={`${id}_button`}
      >
        <Icon name={iconName} iconStyle='light' color='gray' size='small' />
      </button>
    </PanelResizeHandle>
  )
}

// DS breakpoints from useCompactState above drive auto-collapse behavior.

interface CompactState {
  // Below desktop: space is constrained, enforce one-at-a-time behavior.
  isCompact: boolean
  // Below desktop: auto-collapse the right panel.
  collapseRight: boolean
  // Below tablet: auto-collapse the left panel too.
  collapseLeft: boolean
}

const getCompactState = (width: number): CompactState => ({
  isCompact: width < RIGHT_COLLAPSE_BREAKPOINT,
  collapseRight: width < RIGHT_COLLAPSE_BREAKPOINT,
  collapseLeft: width < LEFT_COLLAPSE_BREAKPOINT,
})

const useCompactState = (): CompactState => {
  const [state, setState] = useState<CompactState>(() =>
    getCompactState(
      typeof window !== 'undefined' ? window.innerWidth : Infinity
    )
  )

  useEffect(() => {
    const update = (): void => setState(getCompactState(window.innerWidth))
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return state
}

const ResizablePanelGroup: React.FC<ResizablePanelGroupProps> = ({
  cls = {},
  elmId,
  slots,
  leftPanel,
  rightPanel,
}) => {
  const hasLeft = Boolean(slots?.left && leftPanel)
  const hasRight = Boolean(slots?.right && rightPanel)
  const { isCompact, collapseLeft, collapseRight } = useCompactState()

  const leftPanelRef = useRef<ImperativePanelHandle>(null)
  const rightPanelRef = useRef<ImperativePanelHandle>(null)
  const groupRef = useRef<ImperativePanelGroupHandle>(null)

  const [leftCollapsed, setLeftCollapsed] = useState(
    leftPanel?.defaultCollapsed ?? false
  )
  const [rightCollapsed, setRightCollapsed] = useState(
    rightPanel?.defaultCollapsed ?? false
  )

  const [isDragging, setIsDragging] = useState(false)
  const [isLayoutReady, setIsLayoutReady] = useState(false)

  const autoSaveId = elmId || 'resizable_panel_group'

  // Delay enabling animation and visibility until layout has settled
  useEffect(() => {
    const id = setTimeout(() => setIsLayoutReady(true), 50)
    return () => clearTimeout(id)
  }, [])

  // Sync initial collapsed state after mount
  useEffect(() => {
    if (hasLeft && leftPanel?.defaultCollapsed && leftPanelRef.current) {
      leftPanelRef.current.collapse()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasRight && rightPanel?.defaultCollapsed && rightPanelRef.current) {
      rightPanelRef.current.collapse()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-collapse panels as width crosses breakpoints:
  //  - below desktop: collapse the right panel
  //  - below tablet: collapse the left panel too
  const prevCollapseRight = useRef(collapseRight)
  const prevCollapseLeft = useRef(collapseLeft)
  useEffect(() => {
    if (collapseRight && !prevCollapseRight.current) {
      rightPanelRef.current?.collapse()
    }
    if (collapseLeft && !prevCollapseLeft.current) {
      leftPanelRef.current?.collapse()
    }
    prevCollapseRight.current = collapseRight
    prevCollapseLeft.current = collapseLeft
  }, [collapseRight, collapseLeft])

  const handleLeftCollapse = useCallback(() => setLeftCollapsed(true), [])
  const handleLeftExpand = useCallback(() => setLeftCollapsed(false), [])

  const handleRightCollapse = useCallback(() => setRightCollapsed(true), [])
  const handleRightExpand = useCallback(() => setRightCollapsed(false), [])

  // In compact mode, convert 300px to a percentage of the group width
  const groupId = elmId ? `${elmId}_panel_group` : 'panel_group'
  const getCompactExpandSize = useCallback((): number | undefined => {
    if (!isCompact) return undefined
    const groupEl = getPanelGroupElement(groupId)
    if (!groupEl) return undefined
    const groupWidth = groupEl.offsetWidth
    if (groupWidth <= 0) return undefined
    return Math.min((COMPACT_PANEL_WIDTH_PX / groupWidth) * 100, 80)
  }, [isCompact, groupId])

  return (
    <PanelGroup
      ref={groupRef}
      id={groupId}
      className={cx(
        'panel-group',
        {
          'panel-group--animate': isLayoutReady && !isDragging,
          'panel-group--hidden': !isLayoutReady,
        },
        cls.panelGroup
      )}
      autoSaveId={isCompact ? undefined : autoSaveId}
      direction='horizontal'
      data-elm-id={groupId}
    >
      {hasLeft && (
        <>
          <Panel
            className={cx('panel', 'panel--side')}
            collapsible={leftPanel?.collapsible !== false}
            collapsedSize={0}
            defaultSize={collapseLeft ? 0 : leftPanel?.defaultSize ?? 25}
            minSize={10}
            ref={leftPanelRef}
            order={1}
            onCollapse={handleLeftCollapse}
            onExpand={handleLeftExpand}
          >
            <div className={cx('panel__inner', cls.leftPanel)}>
              {slots?.left?.()}
            </div>
          </Panel>
          <CollapsibleHandle
            panelRef={leftPanelRef}
            isCollapsed={leftCollapsed}
            side='left'
            id={`${groupId}_handle_left`}
            cls={cls}
            onDragging={setIsDragging}
            getExpandSize={getCompactExpandSize}
          />
        </>
      )}

      <Panel className={cx('panel', 'panel--main', cls.mainPanel)} order={2}>
        {slots?.main?.()}
      </Panel>

      {hasRight && (
        <>
          <CollapsibleHandle
            panelRef={rightPanelRef}
            isCollapsed={rightCollapsed}
            side='right'
            id={`${groupId}_handle_right`}
            cls={cls}
            onDragging={setIsDragging}
            getExpandSize={getCompactExpandSize}
          />
          <Panel
            className={cx('panel', 'panel--side')}
            collapsible={rightPanel?.collapsible !== false}
            collapsedSize={0}
            defaultSize={collapseRight ? 0 : rightPanel?.defaultSize ?? 25}
            minSize={10}
            ref={rightPanelRef}
            order={3}
            onCollapse={handleRightCollapse}
            onExpand={handleRightExpand}
          >
            <div className={cx('panel__inner', cls.rightPanel)}>
              {slots?.right?.()}
            </div>
          </Panel>
        </>
      )}
    </PanelGroup>
  )
}

export default ResizablePanelGroup
